/* iter55f96 — Common Traits dialog.
 *
 * Opens when the user starts a new project from a template (built-in
 * catalog, community marketplace, or an imported .docx book template).
 * Presents each character skeleton in the template with a curated
 * trait chip picker (fed by /api/framework/presets) so the writer can
 * personalise the ensemble before their new project boots up.
 *
 * Fully controlled — the parent supplies the character skeletons and
 * consumes `{ traitsByCharacter }` on confirm. The dialog knows nothing
 * about templates, frameworks, or projects — that keeps it reusable.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, X, Sparkles, User } from 'lucide-react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const MAX_TRAITS_PER_CHARACTER = 12;

// Curated fallback so the dialog is usable even if the presets endpoint
// is momentarily unavailable (e.g. cold-start / preview outage).
const FALLBACK_TRAITS = [
  'Brave', 'Loyal', 'Cunning', 'Empathetic', 'Impulsive', 'Stoic',
  'Ambitious', 'Melancholic', 'Sardonic', 'Reserved', 'Charismatic',
  'Ruthless', 'Curious', 'Devout', 'Hopeful', 'Cynical',
];

export default function CommonTraitsDialog({
  open,
  onOpenChange,
  characters,                // [{ id, name, role, traits?: string[] }]
  templateName,
  busy,                       // parent busy flag → disable buttons
  onConfirm,                  // (traitsByCharacter: { [id]: string[] }) => void
}) {
  const [presets, setPresets] = useState(null);
  const [selected, setSelected] = useState({}); // { [id]: Set<string> }
  const [customInput, setCustomInput] = useState({}); // { [id]: string }

  // Fetch presets when the dialog opens (cached across sessions).
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/framework/presets`, { withCredentials: true });
        if (!cancelled) setPresets(r.data);
      } catch { if (!cancelled) setPresets(null); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Seed selections with each character's existing traits when the
  // dialog opens or the character list changes.
  useEffect(() => {
    if (!open) return;
    const next = {};
    for (const c of characters || []) {
      next[c.id] = new Set(Array.isArray(c.traits) ? c.traits : []);
    }
    setSelected(next);
    setCustomInput({});
  }, [open, characters]);

  const options = presets?.character_traits || FALLBACK_TRAITS;

  const toggleTrait = (cid, trait) => {
    setSelected((prev) => {
      const cur = new Set(prev[cid] || []);
      if (cur.has(trait)) cur.delete(trait);
      else if (cur.size < MAX_TRAITS_PER_CHARACTER) cur.add(trait);
      return { ...prev, [cid]: cur };
    });
  };
  const addCustom = (cid) => {
    const raw = (customInput[cid] || '').trim();
    if (!raw) return;
    setSelected((prev) => {
      const cur = new Set(prev[cid] || []);
      if (cur.size < MAX_TRAITS_PER_CHARACTER) cur.add(raw);
      return { ...prev, [cid]: cur };
    });
    setCustomInput((prev) => ({ ...prev, [cid]: '' }));
  };

  const total = Object.values(selected).reduce((n, s) => n + (s?.size || 0), 0);

  const confirm = () => {
    const out = {};
    for (const [cid, set] of Object.entries(selected)) {
      out[cid] = Array.from(set || []);
    }
    onConfirm(out);
  };
  const useDefaults = () => {
    // Skip personalisation — pass an empty map so caller keeps whatever
    // traits the template shipped with.
    onConfirm({});
  };

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="common-traits-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Personalise character traits
          </DialogTitle>
          <DialogDescription>
            {templateName
              ? <>Pick common traits for the characters in <b>{templateName}</b> before we boot your new project.</>
              : <>Pick common traits for each character before we boot your new project.</>}
            {' '}You can always adjust these later from the Character Bible.
          </DialogDescription>
        </DialogHeader>
        <div className="scroll-thin max-h-[65vh] space-y-4 overflow-y-auto px-1 py-2">
          {(!characters || characters.length === 0) ? (
            <div className="rounded-md border border-dashed border-panel-border p-6 text-center text-[12px] text-muted-foreground"
                  data-testid="common-traits-empty">
              This template ships without character skeletons. Skip to boot the project.
            </div>
          ) : characters.map((c) => {
            const chosen = selected[c.id] || new Set();
            const customValues = Array.from(chosen).filter((t) => !options.includes(t));
            const remaining = MAX_TRAITS_PER_CHARACTER - chosen.size;
            return (
              <div
                key={c.id}
                data-testid={`common-traits-char-${c.id}`}
                className="rounded-md border border-panel-border bg-card p-3"
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <User className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-semibold">{c.name || 'Unnamed'}</div>
                    <div className="truncate text-[10.5px] uppercase tracking-wider text-muted-foreground">
                      {c.role || 'Supporting'}
                    </div>
                  </div>
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">
                    {chosen.size}/{MAX_TRAITS_PER_CHARACTER}
                  </span>
                </div>

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {options.map((opt) => {
                    const on = chosen.has(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => toggleTrait(c.id, opt)}
                        disabled={!on && remaining <= 0}
                        data-testid={`common-traits-chip-${c.id}-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition ${
                          on
                            ? 'border-primary bg-primary/15 text-primary'
                            : 'border-border bg-transparent text-muted-foreground hover:text-foreground'
                        } disabled:opacity-40`}
                      >
                        {on ? '✓ ' : '+ '}{opt}
                      </button>
                    );
                  })}
                </div>

                {customValues.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {customValues.map((t) => (
                      <span
                        key={t}
                        data-testid={`common-traits-custom-${c.id}-${t.toLowerCase().replace(/\s+/g, '-')}`}
                        className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-0.5 text-[11px] font-semibold text-primary"
                      >
                        {'\u2713 '}{t}
                        <button
                          type="button"
                          onClick={() => toggleTrait(c.id, t)}
                          className="opacity-70 hover:opacity-100"
                          aria-label={`Remove ${t}`}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-2 flex items-center gap-1">
                  <input
                    type="text"
                    value={customInput[c.id] || ''}
                    onChange={(e) => setCustomInput((prev) => ({ ...prev, [c.id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(c.id); } }}
                    placeholder={`Add custom trait (${remaining} left)`}
                    data-testid={`common-traits-custom-input-${c.id}`}
                    className="flex-1 rounded-sm border border-border bg-background px-2 py-0.5 text-[11px] outline-none"
                    disabled={remaining <= 0}
                  />
                  <button
                    type="button"
                    onClick={() => addCustom(c.id)}
                    data-testid={`common-traits-custom-add-${c.id}`}
                    className="rounded-sm px-1.5 py-0.5 text-[11px] font-semibold text-primary hover:underline disabled:opacity-40"
                    disabled={remaining <= 0}
                  >
                    Add
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="border-t border-border bg-panel-header/40 px-5 py-3">
          <div className="mr-auto text-[10.5px] text-muted-foreground" data-testid="common-traits-total">
            {total} trait{total === 1 ? '' : 's'} selected across {characters?.length || 0} character{(characters?.length || 0) === 1 ? '' : 's'}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={useDefaults}
            disabled={busy}
            data-testid="common-traits-skip"
          >
            Use template defaults
          </Button>
          <Button
            size="sm"
            onClick={confirm}
            disabled={busy}
            data-testid="common-traits-confirm"
            className="gap-1.5"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Boot project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
